/**
 * Workspace icon picker + lookup. Settings stores Lucide-style names
 * (e.g. Building2). Resolve through getWorkspaceIcon so every surface
 * shows the same glyph.
 */

import type { LucideIcon } from 'lucide-react'
import {
  User,
  Building2,
  Briefcase,
  Home,
  Users,
  FolderKanban,
  Rocket,
  Heart,
  Music,
  Circle,
} from 'lucide-react'
import { RubberDuckIcon } from '@/components/icons/rubber-duck-icon'

export type WorkspaceIconOption = {
  value: string
  Icon: LucideIcon
  label: string
}

export const WORKSPACE_ICONS: WorkspaceIconOption[] = [
  { value: 'User', Icon: User, label: 'Personal' },
  { value: 'Building2', Icon: Building2, label: 'Work' },
  { value: 'Briefcase', Icon: Briefcase, label: 'Client' },
  { value: 'Home', Icon: Home, label: 'Home' },
  { value: 'Users', Icon: Users, label: 'Team' },
  { value: 'FolderKanban', Icon: FolderKanban, label: 'Projects' },
  { value: 'Rocket', Icon: Rocket, label: 'Product' },
  { value: 'Heart', Icon: Heart, label: 'Nonprofit' },
  { value: 'Music', Icon: Music, label: 'Creative' },
  { value: 'RubberDuck', Icon: RubberDuckIcon, label: 'Hop duck' },
]

const ICON_BY_VALUE: Record<string, LucideIcon> = Object.fromEntries(
  WORKSPACE_ICONS.map(({ value, Icon }) => [value, Icon]),
)

/** Extra keys from older sidebar lookups (lowercase / type aliases). */
const ICON_ALIASES: Record<string, LucideIcon> = {
  building2: Building2,
  building: Building2,
  work: Building2,
  briefcase: Briefcase,
  user: User,
  personal: User,
  user2: User,
  home: Home,
  users: Users,
  folderkanban: FolderKanban,
  rocket: Rocket,
  heart: Heart,
  music: Music,
  rubberduck: RubberDuckIcon,
  circle: Circle,
}

export const WORKSPACE_ICON_MAP: Record<string, LucideIcon> = {
  ...ICON_BY_VALUE,
  ...ICON_ALIASES,
}

export function getWorkspaceIcon(
  icon: string | null | undefined,
  fallback: LucideIcon = Building2,
): LucideIcon {
  if (!icon) return fallback
  const exact = WORKSPACE_ICON_MAP[icon]
  if (exact) return exact
  const normalized = icon.toLowerCase().replace(/[-_\s]/g, '')
  return WORKSPACE_ICON_MAP[normalized] ?? fallback
}
